import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {withRouter} from 'react-router-dom';
import {AutoSizer, List as VirtualizedList, CellMeasurer, CellMeasurerCache} from 'react-virtualized';

import * as api from '../../lib/api';
import {withUser} from '../../lib/user';
import Button from '../../components/Button';
import SideModal from '../../components/SideModal';
import SheetCard from '../../components/SheetCard';
import {T} from '../../components/I18n';
import { DEFAULT_SHEETS, MAX_SHEETS } from './shared';
import Input from '../../components/Input';
import DateTimeInput from '../../components/DateTimeInput';
import search from '../../lib/search';
import Notification from '../../components/Notification';

class SheetSelector extends PureComponent {
  cache = new CellMeasurerCache({
    defaultHeight: 193,
    fixedWidth: true
  });

  state = {
    sheets: [],
    selectedById: {},
    loading: true,
    failed: false,
    startDate: null,
    endDate: null,
    searchQuery: ''
  };

  async componentDidMount() {
    const {template, selectedIds, credentials} = this.props;
    
    try {
      const sheets = await api.getAllSheets(template.id, credentials);
      let selectedById = {};

      if (selectedIds[0] === 'all') {
        // The first DEFAULT_SHEETS are selected.
        for (let i = 0; i < Math.min(DEFAULT_SHEETS, sheets.length); i++) {
          const sheet = sheets[i];
          selectedById[sheet.id] = true;
        }
      }
      else {
        selectedById = selectedIds.reduce((map, sheetId) => {
          map[sheetId] = true;
          return map;
        }, {});
      }

      this.setState({
        sheets,
        selectedById
      });
    }
    catch (e) {
      this.setState({failed: true});
    }
    finally {
      this.setState({loading: false});
    }
  }

  handleSelect = (sheet, isSelected) => {
    this.setState(prevState => ({
      selectedById: {
        ...prevState.selectedById,
        [sheet.id]: isSelected
      }
    }));
  }

  handleClear = () => {
    this.setState(prevState => {
      let {sheets, searchQuery, startDate, endDate} = prevState;
      sheets = getFilteredSheets(sheets, searchQuery, startDate, endDate);

      return {
        selectedById: sheets.reduce((map, sheet) => {
          map[sheet.id] = false;
          return map;
        }, {...prevState.selectedById})
      };
    });
  }

  handleSelectAll = () => {
    this.setState(prevState => {
      let {sheets, searchQuery, startDate, endDate} = prevState;
      sheets = getFilteredSheets(sheets, searchQuery, startDate, endDate);

      return {
        selectedById: sheets.reduce((map, sheet) => {
          map[sheet.id] = true;
          return map;
        }, {...prevState.selectedById})
      };
    });
  }

  handleSearchChange = (e) => {
    this.setState({searchQuery: e.target.value});
  }

  handleStartDateChange = (startDate) => {
    this.setState({startDate});
  }

  handleEndDateChange = (endDate) => {
    this.setState({endDate});
  }

  handleApply = () => {
    const {history, location: {pathname}, template, onClose} = this.props;
    const {selectedById} = this.state;
    const sheetIds = Object.keys(selectedById).filter(key => selectedById[key]);

    onClose();
    history.push(`/sheet/${template.id}/${sheetIds.join(',')}${pathname.indexOf('/data') >= 0 ? '/data' : ''}`);
  }

  render() {
    const {open, onClose} = this.props;
    let {sheets, selectedById, loading, failed, startDate, endDate, searchQuery} = this.state;

    if (loading || failed) {
      return null;
    }

    const selectedSheets = Object.keys(selectedById).filter(key => selectedById[key]);

    // Filter the sheets based on search criteria.
    sheets = getFilteredSheets(sheets, searchQuery, startDate, endDate);

    return (
      <SideModal
        title={<T isolate>Select Sheets (<T ignore>{selectedSheets.length}</T>)</T>}
        header={
          <div>
            <Input type="text" placeholder="Search sheets by name or username..." value={searchQuery} onChange={this.handleSearchChange} />
            <SheetDateFilters>
              <DateTimeInput inline placeholder="Start date" date={startDate} onChange={this.handleStartDateChange} />
              <DateTimeInput inline placeholder="End date" date={endDate} onChange={this.handleEndDateChange} />
            </SheetDateFilters>  
          </div>
        }
        footer={selectedSheets.length > MAX_SHEETS && (
          <Footer>
            <Notification mini warning>The maximum number of sheets you can select is {MAX_SHEETS}.</Notification>
          </Footer>
        )}
        open={open}
        onClose={onClose}
        options={[
          <Button disabled={selectedSheets.length < 1} onClick={this.handleApply}>Apply</Button>,
          <Button secondary onClick={this.handleClear}>Clear All</Button>,
          <Button secondary onClick={this.handleSelectAll}>Select All</Button>
        ]}>
        <AutoSizer>
          {({width, height}) => (
            <VirtualizedList
              ref={node => this.list = node}
              height={height}
              width={width}
              style={{overflowX: 'hidden'}}
              rowCount={sheets.length}
              rowHeight={this.cache.rowHeight}
              deferredMeasurementCache={this.cache}
              overscanRowCount={10}
              rowRenderer={({index, key, parent, style}) => {
                const sheet = sheets[index];
                
                return (
                  <CellMeasurer cache={this.cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
                    <div style={style}>
                      <CardContainer index={index}>
                        <SheetCard
                          sheet={sheet}
                          active={selectedById[sheet.id]}
                          action={
                            <Button onClick={() => this.handleSelect(sheet, !selectedById[sheet.id])}>
                              {selectedById[sheet.id] ? 'Unselect' : 'Select'}
                            </Button>
                          }
                        />
                      </CardContainer>
                    </div>
                  </CellMeasurer>
                );
              }}
            />
          )}
        </AutoSizer>
      </SideModal>
    );
  }
}

function getFilteredSheets(sheets, searchQuery, startDate, endDate) {
  if (searchQuery) {
    sheets = sheets.filter(sheet => search(sheet.name, searchQuery) || search(sheet.user.userName, searchQuery));
  }

  if (startDate) {
    sheets = sheets.filter(sheet => sheet.dates.lastModified >= startDate.getTime());
  }

  if (endDate) {
    sheets = sheets.filter(sheet => sheet.dates.lastModified <= endDate.getTime());
  }

  return sheets;
}

const CardContainer = styled.div`
  margin: 1.875rem;
  margin-top: 0;

  ${props => props.index === 0 && `
    margin-top: 1.875rem;
  `}
`;

const SheetDateFilters = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 1rem;
`;

const Footer = styled.div`
  padding: 1.25rem 1.875rem 0 1.875rem;
`;

export default withRouter(withUser(SheetSelector));